import type { Player, Team } from '../../store/config';
import { Button, Select, TextInput } from '../../ui';
import type { Fixture } from './FixtureTable';

export function localDateTime(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function FixtureReview({
  fixtures,
  teams,
  players,
  onChange,
  disabled,
}: {
  fixtures: Fixture[];
  teams: Team[];
  players: Player[];
  onChange: (fixtures: Fixture[]) => void;
  disabled: boolean;
}) {
  const name = (id: string) =>
    players.find((p) => p.steamId === id)?.name || id;
  const update = (fixture: Fixture) =>
    onChange(fixtures.map((f) => (f.id === fixture.id ? fixture : f)));
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-semibold">
          Review your {fixtures.length}{' '}
          {fixtures.length === 1 ? 'match' : 'matches'}
        </h3>
        <p className="mt-1 text-sm text-ink-dim">
          We selected the first players in each team as starters. Open a match
          to check or change its lineup. Saving adds drafts to your schedule;
          you choose when to queue them.
        </p>
      </div>
      {fixtures.map((f) => (
        <details
          key={f.id}
          className="rounded-lg border border-line bg-surface-2/40"
        >
          <summary className="cursor-pointer p-4 text-sm">
            <span className="font-semibold">
              {f.team_a_name} vs {f.team_b_name}
            </span>
            <span className="ml-3 text-xs text-ink-dim">
              Round {f.round} · {f.settings.format.toUpperCase()}
              {f.leg > 1 ? ' · Return match' : ''}
            </span>
          </summary>
          <fieldset
            disabled={disabled}
            className="space-y-4 border-t border-line p-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm">
                Match name
                <TextInput
                  value={f.settings.game_name}
                  onChange={(e) =>
                    update({
                      ...f,
                      settings: { ...f.settings, game_name: e.target.value },
                    })
                  }
                />
              </label>
              <label className="space-y-1 text-sm">
                Start time (your local time)
                <TextInput
                  type="datetime-local"
                  value={localDateTime(f.scheduled_at)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value || !Number.isNaN(new Date(value).getTime()))
                      update({
                        ...f,
                        scheduled_at: value
                          ? new Date(value).toISOString()
                          : undefined,
                      });
                  }}
                />
                <span className="block text-xs text-ink-dim">
                  Optional. Leave empty to start when queued.
                </span>
              </label>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {(['radiant', 'dire'] as const).map((side) => {
                const teamID = side === 'radiant' ? f.team_a_id : f.team_b_id;
                const teamName =
                  side === 'radiant' ? f.team_a_name : f.team_b_name;
                const starters = f.settings.players_by_team[side];
                const choices = [
                  ...new Set([
                    ...(teams.find((t) => t.id === teamID)?.playerSteamIds ??
                      []),
                    ...starters,
                  ]),
                ];
                return (
                  <fieldset key={side} className="space-y-2">
                    <legend className="mb-2 text-sm font-medium">
                      {teamName} starters
                    </legend>
                    {starters.map((id, slot) => (
                      <Select
                        key={slot}
                        aria-label={`${teamName} starter ${slot + 1}`}
                        value={id}
                        onChange={(e) =>
                          update({
                            ...f,
                            settings: {
                              ...f.settings,
                              players_by_team: {
                                ...f.settings.players_by_team,
                                [side]: starters.map((old, index) =>
                                  index === slot ? e.target.value : old,
                                ),
                              },
                            },
                          })
                        }
                      >
                        {choices.map((choice) => (
                          <option
                            key={choice}
                            value={choice}
                            disabled={
                              choice !== id && starters.includes(choice)
                            }
                          >
                            {name(choice)}
                          </option>
                        ))}
                      </Select>
                    ))}
                  </fieldset>
                );
              })}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                onChange(fixtures.filter((other) => other.id !== f.id))
              }
            >
              Remove this match
            </Button>
          </fieldset>
        </details>
      ))}
    </div>
  );
}
